import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames';

import debounce from '@/utils/debounce';

let mouseX, mouseY;

export default function CtfProductTile({ fields }) {
  return (
    <ProductTile id={fields?.id}>
      <img
        className={cls(
          'aaco-product-tile__image',
          'aaco-product-tile__image--default',
          'w-[273px] md:w-[400px] lg:w-[470px] h-full',
          'object-fill'
        )}
        src={fields?.defaultImage?.[0]?.secure_url}
        alt="product-tile__default-image"
      />
      <img
        className={cls(
          'aaco-product-tile__image',
          'aaco-product-tile__image--hover',
          'w-[273px] md:w-[400px] lg:w-[470px] h-full',
          'object-fill'
        )}
        src={fields?.hoverImage?.[0]?.secure_url}
        alt="product-tile__hover-image"
      />
    </ProductTile>
  );
}
CtfProductTile.propTypes = {
  entries: PropTypes.array,
  fields: PropTypes.shape({
    defaultImage: PropTypes.array,
    hoverImage: PropTypes.array,
    id: PropTypes.string,
  }),
};

export function ProductTile({ id, children = [] }) {
  const productTileRef = useRef();

  const [hover, setHover] = useState(false);
  const toggleHover = () => setHover(!hover);
  const showHover = () => setHover(true);
  const removeHover = () => setHover(false);

  const showDefaultImage = !(hover && children?.[1]);
  const showHoverImage = hover && children?.[1];

  useEffect(() => {
    const handleScroll = debounce(() => {
      if (!document?.elementFromPoint) return;
      if (typeof mouseX === 'undefined' || typeof mouseY === 'undefined')
        return;
      const hoverTarget = document.elementFromPoint(mouseX, mouseY);
      if (hoverTarget) {
        if (productTileRef?.current === hoverTarget && !hover) {
          showHover();
        } else if (
          hover &&
          !hoverTarget?.classList?.contains('aaco-product-tile__image')
        ) {
          removeHover();
        }
      }
    }, 5);

    const handleMouseMove = (event) => {
      mouseX = event?.clientX;
      mouseY = event?.clientY;
    };

    if (productTileRef?.current && window?.document) {
      document.addEventListener('mousemove', handleMouseMove, {
        passive: true,
      });
      document.addEventListener('scroll', handleScroll, {
        passive: true,
      });
    }

    return () => {
      if (productTileRef?.current && window?.document) {
        productTileRef?.current.removeEventListener('scroll', handleScroll, {
          passive: true,
        });
        document.addEventListener('mousemove', handleMouseMove, {
          passive: true,
        });
      }
    };
  }, [productTileRef]);

  if (!children?.length) {
    return null;
  }

  return (
    <div className="aaco-product-tile h-[204px] md:h-[349px]" id={id}>
      {/* START: desktop */}
      <div
        className="aaco-product-tile--desktop hidden md:block md:relative md:w-screen md:h-full md:z-10"
        ref={productTileRef}
        onMouseOver={showHover}
        onMouseLeave={removeHover}
      >
        <div
          className={cls(
            'aaco-product-tile--hover',
            'absolute top-0 left-0 transition-opacity duration-300 ease-out',
            {
              'opacity-100': showHoverImage,
              'opacity-0': !showHoverImage,
            }
          )}
        >
          {children?.[1]}
        </div>
        <div
          className={cls(
            'aaco-product-tile--default',
            'absolute top-0 left-0 transition-opacity duration-300 ease-out',
            {
              'opacity-0': !showDefaultImage,
              'opacity-100': showDefaultImage,
            }
          )}
        >
          {children?.[0]}
        </div>
      </div>
      {/* END: desktop */}

      {/* START: mobile */}
      <div
        className="aaco-product-tile--mobile relative md:hidden"
        onClick={toggleHover}
        role="button"
      >
        <div
          className={cls(
            'aaco-product-tile--active',
            'absolute top-0 left-0 transition-opacity duration-300 ease-out',
            {
              'opacity-100': showHoverImage,
              'opacity-0': !showHoverImage,
            }
          )}
        >
          {children?.[1]}
        </div>
        <div
          className={cls(
            'aaco-product-tile--default',
            'absolute top-0 left-0 transition-opacity duration-300 ease-out',
            {
              'opacity-0': !showDefaultImage,
              'opacity-100': showDefaultImage,
            }
          )}
        >
          {children?.[0]}
        </div>
      </div>
      {/* END: mobile */}
    </div>
  );
}

ProductTile.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node,
};
